import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 20, right: 20, bottom: 55, left: 65 };
const CHART_HEIGHT = 420;

const CLASSIFICATION_BADGES = {
  stable: { bg: '#dcfce7', color: '#166534', label: 'STABLE' },
  periodic: { bg: '#fef3c7', color: '#92400e', label: 'PERIODIC' },
  chaotic: { bg: '#fee2e2', color: '#991b1b', label: 'CHAOTIC' },
};

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '4px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 8px',
    borderRadius: '9999px',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.025em',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '12px',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '260px',
  },
  lyapunovLabel: {
    fontSize: '11px',
    color: '#9ca3af',
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
  },
};

export default function PhasePortraitChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.points || data.points.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(400, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', CHART_HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // -- Scales --
    const allX = data.points.map((p) => p.x);
    const allY = data.points.map((p) => p.y);
    const xExtent = d3.extent(allX);
    const yExtent = d3.extent(allY);

    // Use diagonal range too if provided
    const globalMin = Math.min(
      xExtent[0],
      yExtent[0],
      data.diagonal ? data.diagonal.min : Infinity
    );
    const globalMax = Math.max(
      xExtent[1],
      yExtent[1],
      data.diagonal ? data.diagonal.max : -Infinity
    );

    const pad = (globalMax - globalMin) * 0.08 || 1;

    const x = d3.scaleLinear()
      .domain([globalMin - pad, globalMax + pad])
      .nice()
      .range([0, innerW]);

    const y = d3.scaleLinear()
      .domain([globalMin - pad, globalMax + pad])
      .nice()
      .range([innerH, 0]);

    // -- Grid lines --
    const xTicks = x.ticks();
    const yTicks = y.ticks();

    g.selectAll('.grid-h')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    g.selectAll('.grid-v')
      .data(xTicks)
      .enter()
      .append('line')
      .attr('x1', (d) => x(d))
      .attr('x2', (d) => x(d))
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // -- Diagonal y=x reference line (dashed) --
    const diagMin = data.diagonal ? data.diagonal.min : globalMin - pad;
    const diagMax = data.diagonal ? data.diagonal.max : globalMax + pad;

    g.append('line')
      .attr('x1', x(diagMin))
      .attr('y1', y(diagMin))
      .attr('x2', x(diagMax))
      .attr('y2', y(diagMax))
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '6,4');

    // Label for diagonal
    g.append('text')
      .attr('x', x(diagMax) - 4)
      .attr('y', y(diagMax) - 6)
      .attr('text-anchor', 'end')
      .attr('font-size', '10px')
      .attr('fill', '#9ca3af')
      .text('y = x (no change)');

    // -- Arrow marker definition --
    const defs = svg.append('defs');

    // Color scale for trajectory: light (old) to dark (recent)
    const n = data.trajectory ? data.trajectory.length : 0;
    const colorScale = d3.scaleSequential()
      .domain([0, Math.max(1, n - 2)])
      .interpolator(d3.interpolateRgb('#93c5fd', '#1e3a5f'));

    // Create arrow markers for each segment color
    if (data.trajectory && data.trajectory.length > 1) {
      const numSegments = data.trajectory.length - 1;

      for (let i = 0; i < numSegments; i++) {
        const segColor = colorScale(i);
        defs.append('marker')
          .attr('id', `arrow-${i}`)
          .attr('viewBox', '0 0 10 10')
          .attr('refX', 8)
          .attr('refY', 5)
          .attr('markerWidth', 6)
          .attr('markerHeight', 6)
          .attr('orient', 'auto')
          .append('path')
          .attr('d', 'M 0 2 L 8 5 L 0 8 Z')
          .attr('fill', segColor);
      }

      // -- Trajectory segments with arrows --
      for (let i = 0; i < numSegments; i++) {
        const p0 = data.trajectory[i];
        const p1 = data.trajectory[i + 1];
        const segColor = colorScale(i);

        // Compute shortened line so arrow doesn't overlap the dot
        const x0 = x(p0[0]);
        const y0 = y(p0[1]);
        const x1 = x(p1[0]);
        const y1 = y(p1[1]);
        const dx = x1 - x0;
        const dy = y1 - y0;
        const len = Math.sqrt(dx * dx + dy * dy);

        if (len < 2) continue; // skip degenerate segments

        // Shorten end by 5px so arrow tip sits outside the dot
        const shortenEnd = Math.min(5, len * 0.3);
        const ratio = (len - shortenEnd) / len;
        const ex = x0 + dx * ratio;
        const ey = y0 + dy * ratio;

        g.append('line')
          .attr('x1', x0)
          .attr('y1', y0)
          .attr('x2', ex)
          .attr('y2', ey)
          .attr('stroke', segColor)
          .attr('stroke-width', 1.5)
          .attr('stroke-opacity', 0.7)
          .attr('marker-end', `url(#arrow-${i})`);
      }
    }

    // -- Scatter points with color gradient --
    const pointColorScale = d3.scaleSequential()
      .domain([0, Math.max(1, data.points.length - 1)])
      .interpolator(d3.interpolateRgb('#93c5fd', '#1e3a5f'));

    data.points.forEach((pt, i) => {
      g.append('circle')
        .attr('cx', x(pt.x))
        .attr('cy', y(pt.y))
        .attr('r', 5)
        .attr('fill', pointColorScale(i))
        .attr('stroke', '#fff')
        .attr('stroke-width', 1.5)
        .attr('opacity', 0.9)
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          d3.select(event.currentTarget)
            .attr('r', 8)
            .attr('stroke-width', 2);
          const change = pt.y - pt.x;
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            date: pt.date,
            priceT: pt.x,
            priceT1: pt.y,
            change,
          });
        })
        .on('mouseleave', (event) => {
          d3.select(event.currentTarget)
            .attr('r', 5)
            .attr('stroke-width', 1.5);
          setTooltip(null);
        });
    });

    // -- Fixed points (equilibria) --
    if (data.fixedPoints && data.fixedPoints.length > 0) {
      data.fixedPoints.forEach((fp) => {
        const cx = x(fp.x);
        const cy = y(fp.y);
        const isStable = fp.type === 'stable';
        const fpColor = isStable ? '#16a34a' : '#dc2626';

        // Diamond / star symbol
        const size = 8;
        // Draw a diamond
        const diamondPath = [
          [cx, cy - size],       // top
          [cx + size, cy],       // right
          [cx, cy + size],       // bottom
          [cx - size, cy],       // left
        ];
        const pathD = `M ${diamondPath.map((p) => p.join(',')).join(' L ')} Z`;

        g.append('path')
          .attr('d', pathD)
          .attr('fill', fpColor)
          .attr('fill-opacity', 0.25)
          .attr('stroke', fpColor)
          .attr('stroke-width', 2);

        // Inner dot
        g.append('circle')
          .attr('cx', cx)
          .attr('cy', cy)
          .attr('r', 2.5)
          .attr('fill', fpColor);

        // Label
        g.append('text')
          .attr('x', cx + size + 4)
          .attr('y', cy + 4)
          .attr('font-size', '10px')
          .attr('font-weight', 500)
          .attr('fill', fpColor)
          .text(isStable ? 'Stable eq.' : 'Unstable eq.');
      });
    }

    // -- Axes --

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(
        d3.axisBottom(x)
          .ticks(8)
          .tickSize(0)
          .tickFormat((d) => `$${d}`)
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // X axis label
    g.append('text')
      .attr('x', innerW / 2)
      .attr('y', innerH + 40)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text('Price at time t');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .ticks(8)
          .tickSize(0)
          .tickFormat((d) => `$${d}`)
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Y axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerH / 2)
      .attr('y', -50)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text('Price at time t+1');

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Empty state
  if (!data || !data.points || data.points.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.headerRow}>
          <div style={styles.title}>Phase Portrait</div>
        </div>
        <div style={styles.subtitle}>
          Price dynamics in phase space — attractors and stability
        </div>
        <div style={styles.emptyState}>No phase portrait data available</div>
      </div>
    );
  }

  const classification = data.classification || 'stable';
  const badge = CLASSIFICATION_BADGES[classification] || CLASSIFICATION_BADGES.stable;

  return (
    <div style={styles.card}>
      <div style={styles.headerRow}>
        <div style={styles.title}>Phase Portrait</div>
        <span
          style={{
            ...styles.badge,
            background: badge.bg,
            color: badge.color,
          }}
        >
          {badge.label}
        </span>
        {data.lyapunov != null && (
          <span style={styles.lyapunovLabel}>
            &lambda; = {data.lyapunov.toFixed(3)}
          </span>
        )}
      </div>
      <div style={styles.subtitle}>
        Price dynamics in phase space — attractors and stability
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <circle cx="5" cy="5" r="4" fill="#93c5fd" stroke="#fff" strokeWidth="1" />
            <circle cx="15" cy="5" r="4" fill="#1e3a5f" stroke="#fff" strokeWidth="1" />
          </svg>
          <span>Old → Recent</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="14" y2="5" stroke="#6b7280" strokeWidth="1.5" />
            <polygon points="14,2 20,5 14,8" fill="#6b7280" />
          </svg>
          <span>Trajectory</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="8" x2="20" y2="2" stroke="#d1d5db" strokeWidth="1.5" strokeDasharray="4,3" />
          </svg>
          <span>y = x (no change)</span>
        </div>
        {data.fixedPoints && data.fixedPoints.length > 0 && (
          <div style={styles.legendItem}>
            <svg width="16" height="16" viewBox="0 0 16 16">
              <path d="M 8 1 L 15 8 L 8 15 L 1 8 Z" fill="#16a34a" fillOpacity="0.25" stroke="#16a34a" strokeWidth="1.5" />
            </svg>
            <span>Fixed point</span>
          </div>
        )}
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {(() => {
              const date = new Date(tooltip.date);
              const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
            })()}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Price(t)</span>
            <span style={{ fontWeight: 600 }}>${tooltip.priceT.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Price(t+1)</span>
            <span style={{ fontWeight: 600 }}>${tooltip.priceT1.toFixed(2)}</span>
          </div>
          <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '4px', paddingTop: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span style={{ color: '#6b7280' }}>Change</span>
              <span
                style={{
                  fontWeight: 600,
                  color: tooltip.change > 0 ? '#16a34a' : tooltip.change < 0 ? '#dc2626' : '#6b7280',
                }}
              >
                {tooltip.change >= 0 ? '+' : ''}${tooltip.change.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
